import clamp from 'lodash/clamp';

import {localiseHostName} from 'utils/host-utils';

const DEFAULT_SOURCE_SET_SIZES = [400, 800, 1200, 1600];
const DEFAULT_SOURCE_SIZE = 400;

/**
 * Computes dimensions and position of a child needed for it to fit the parent.
 *
 * Works exactly as `background-position` and `object-position` do in CSS.
 *
 * Example of `kx` an `ky` values: if the desired position is 50% by 75%, you
 * should provide 0.5 and 0.75 as their values.
 *
 * @param {string} fit              How the child should be resized to fit the parent ('cover' or 'contain').
 * @param {string} kx               How the child should be positioned to fit the parent horizontally.
 * @param {string} ky               How the child should be positioned to fit the parent vertically.
 * @param {number} parentWidth      The width of the parent.
 * @param {number} parentHeight     The height of the parent.
 * @param {number} childAspectRatio The aspect ratio of the child.
 *
 * @return {Object} Computed dimensions and position of the child.
 */
export const getFittingRect = (fit, kx, ky, parentWidth, parentHeight, childAspectRatio) => {
  const containerAspectRatio = parentWidth / parentHeight;

  if (fit === 'cover') {
    if (containerAspectRatio > childAspectRatio) {
      const childHeight = parentWidth / childAspectRatio;

      return {
        width: parentWidth,
        height: childHeight,
        x: 0,
        y: ky * (parentHeight - childHeight)
      };
    }

    const childWidth = parentHeight * childAspectRatio;

    return {
      width: childWidth,
      height: parentHeight,
      x: kx * (parentWidth - childWidth),
      y: 0
    };
  }

  if (containerAspectRatio > childAspectRatio) {
    const childWidth = parentHeight * childAspectRatio;

    return {
      width: childWidth,
      height: parentHeight,
      x: kx * (parentWidth - childWidth),
      y: 0
    };
  }

  const childHeight = parentWidth / childAspectRatio;

  return {
    width: parentWidth,
    height: childHeight,
    x: 0,
    y: ky * (parentHeight - childHeight)
  };
};

/**
 * Generates the src and srcSet properties for an image with transformations
 * from its metadata applied.
 *
 * @param {number} containerAspectRatio The aspect ratio of the image's container.
 * @param {number} [imageAspectRatio]   The aspect ratio of the image.
 * @param {number} [url]                The original URL of the image.
 * @param {Object} [metadata]           The image's metadata.
 *
 * @return {?Object} An object containing the src and srcSet properties,
 *                   or null if url was not provided.
 */
export const getImageSourceProps = (containerAspectRatio, imageAspectRatio, url, metadata) => {
  const normalizedUrl = normalizeUrl(url);
  let limitBy;

  if (!url) {
    return null;
  }

  if (imageAspectRatio) {
    const shouldScaleToFit = Boolean(metadata && metadata.contain);
    limitBy = containerAspectRatio < imageAspectRatio === shouldScaleToFit ? 'width' : 'height';
  } else {
    limitBy = 'width';
  }

  const urlWithTransformations = applyMetadata(normalizedUrl, metadata, imageAspectRatio);

  return {
    src: applySizeLimit(urlWithTransformations, {[limitBy]: DEFAULT_SOURCE_SIZE}),
    srcSet: DEFAULT_SOURCE_SET_SIZES.map(size => {
      const url = applySizeLimit(urlWithTransformations, {[limitBy]: size});
      const width = Math.round(limitBy === 'width' ? size : size / containerAspectRatio);
      return `${url} ${width}w`;
    }).join(', ')
  };
};

/**
 * Computes the expected image aspect ratio.
 *
 * @param {Object} crop         The crop rectangle from metadata.
 * @param {number} [rotation=0] The rotation amount in degrees.
 *
 * @return {number} The expected aspect ratio.
 */
export const getImageAspectRatio = ({w, h}, rotation = 0) => {
  if (rotation === 90 || rotation === 270) {
    return h / w;
  }

  return w / h;
};

/**
 * Rotates the crop rectange from metadata.
 *
 * @param {Object} crop           The crop rectangle from metadata.
 * @param {number} originalWidth  The width of the original image.
 * @param {number} originalHeight The height of the original image.
 * @param {number} [rotation=0]   The rotation amount in degrees.
 *
 * @return {Object} A new crop rectangle with the rotation applied.
 */
export const rotateCropRectangle = ({x, y, w, h}, originalWidth, originalHeight, rotation = 0) => {
  if (rotation === 90) {
    return {x: y, y: originalHeight - x - w, w: h, h: w};
  }

  if (rotation === 180) {
    return {x: originalWidth - x - w, y: originalHeight - y - h, w, h};
  }

  if (rotation === 270) {
    return {x: originalWidth - y - h, y: x, w: h, h: w};
  }

  return {x, y, w, h};
};

/**
 * Rotates the focus point from metadata.
 *
 * @param {Object}  focus            The focus point from metadata.
 * @param {number}  [rotation=0]     The rotation amount in degrees.
 * @param {boolean} [clockwise=true] Whether the rotation should be clockwise.
 *
 * @return {Object} A new focus point with the rotation applied.
 */
export const rotateFocusPoint = ({kx, ky}, rotation = 0, clockwise = true) => {
  rotation = clockwise ? rotation : 360 - rotation;

  if (rotation === 90) {
    return {kx: 1 - ky, ky: kx};
  }

  if (rotation === 180) {
    return {kx: 1 - kx, ky: 1 - ky};
  }

  if (rotation === 270) {
    return {kx: ky, ky: 1 - kx};
  }

  return {kx, ky};
};

/**
 * Finds a background-position / object-position value that will center the provided coordinate.
 *
 * @param {number} k     The relative coordinate on the image's scale.
 * @param {number} scale How many times the image's scale is larger than the container's one.
 *
 * @return {number} The relative background-position / object-position value.
 */
export const centerCoordinate = (k, scale) => {
  if (scale === 1) {
    return 0.5;
  }

  return clamp((k * scale - 0.5) / (scale - 1), 0, 1);
};

/**
 * Converts the focus point to background-position / object-position coordinates that center it.
 *
 * @param {number} containerAspectRatio The aspect ratio of the image's container.
 * @param {number} imageAspectRatio     The aspect ratio of the image.
 * @param {Object} [metadata]           The image's metadata.
 *
 * @return {Object} A new focus point with the conversion applied.
 */
export const convertFocusPointToCss = (containerAspectRatio, imageAspectRatio, metadata) => {
  const convertedFocus = {kx: 0.5, ky: 0.5};

  if (!metadata || !metadata.focus || metadata.contain) {
    return convertedFocus;
  }

  const {kx, ky} = rotateFocusPoint(metadata.focus, metadata.rotation);

  if (containerAspectRatio > imageAspectRatio) {
    convertedFocus.ky = centerCoordinate(ky, containerAspectRatio / imageAspectRatio);
  } else {
    convertedFocus.kx = centerCoordinate(kx, imageAspectRatio / containerAspectRatio);
  }

  return convertedFocus;
};

/**
 * Constructs the URL of an image with size limitations applied.
 *
 * @param {string} url             The original URL of the image.
 * @param {Object} [limits]
 * @param {number} [limits.width]  The maximum width.
 * @param {number} [limits.height] The maximum width.
 *
 * @return {?string} A new URL with transformations applied
 *                   or null if no url was provided.
 */
export const applySizeLimit = (url, {width, height} = {}) => {
  if (!url) {
    return null;
  }

  const params = [];

  if (width) {
    params.push(`w_${width}`);
  }

  if (height) {
    params.push(`h_${height}`);
  }

  if (params.length) {
    return url.replace(/(\/v\d)/, `/${params.join(',')},c_limit/f_auto,q_auto:best$1`);
  }

  return url;
};

/**
 * Constructs the URL of an image with transformations from its metadata applied.
 *
 * @param {number} [url]      The original URL of the image.
 * @param {Object} [metadata] The image's metadata.
 *
 * @return {?string} A new URL with transformations applied,
 *                   or null if no url was provided.
 */
export const applyMetadata = (url, metadata, imageAspectRatio) => {
  if (!url) {
    return null;
  }

  if (!metadata) {
    return url;
  }

  const isSvg = url.slice(-4) === '.svg';
  const transformations = [];
  const {crop, rotation, contain} = metadata;

  if (crop && !isSvg) {
    const {x, y, w, h} = crop;
    transformations.push(`x_${x},y_${y},w_${w},h_${h},c_crop`);
  }

  transformations.push(`a_${rotation ? rotation : 'exif'}`);

  if (contain) {
    transformations.push(`ar_${imageAspectRatio || 4 / 3},c_pad`);
  }

  if (transformations.length) {
    return url.replace('/upload/', `/upload/${transformations.join('/')}/`);
  }

  return url;
};

/**
 * I don't know what this is and if it's ever needed. Just a straight copy-paste
 * from the previous version.
 * - @borodean
 */
export const normalizeUrl = urlString => {
  try {
    const url = new URL(urlString);

    if (!url.href.includes('/upload/')) {
      return null;
    }

    url.protocol = 'https:';
    url.hostname = localiseHostName(url.hostname);
    url.pathname = url.pathname.replace(/\.mov|\.mp4/gi, '.jpg');

    return url.href;
  } catch (e) {
    return null;
  }
};
